import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type AnimationEvent,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { Tooltip } from "@/components/tooltip";
import {
  downloadNotificationFile,
  listNotifications,
  markNotificationRead,
  type AppNotification,
} from "@/src/services/notifications_service";
import "./notifications-popover.css";

export type Notification = AppNotification;

type NotificationsPopoverProps = {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  isMobile?: boolean;
  scrimInsetLeft?: number;
  onUnreadCount?: (count: number) => void;
  reloadKey?: number;
};

const EDGE_GAP = 12;
const FLYOUT_WIDTH = 480;
const FLYOUT_MIN_HEIGHT = "min(480px, calc(100vh - 24px))";
const FLYOUT_MAX_HEIGHT = "min(560px, calc(100vh - 24px))";

export function NotificationsPopover({
  open,
  onClose,
  anchorEl,
  isMobile = false,
  scrimInsetLeft = 0,
  onUnreadCount,
  reloadKey,
}: NotificationsPopoverProps) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    setStatus("loading");
    const res = await listNotifications();
    if (res.ok) {
      setItems(res.value);
      setStatus("idle");
      onUnreadCount?.(res.value.filter((n) => !n.read).length);
    } else {
      setStatus("error");
    }
  }, [onUnreadCount]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (open) void load();
  }, [open, load, reloadKey]);

  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current.focus();
      restoreFocusRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !panelEl) return;
    panelEl.focus();

    function focusables() {
      return Array.from(
        panelEl!.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        ),
      );
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, panelEl, onClose]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const inPanel = panelEl?.contains(e.target as Node);
      const inAnchor = anchorEl?.contains(e.target as Node);
      if (!inPanel && !inAnchor) onClose();
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, panelEl, anchorEl, onClose]);

  useEffect(() => {
    if (!(open && isMobile)) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, isMobile]);

  useLayoutEffect(() => {
    if (isMobile || !open || !panelEl) {
      setPos(null);
      return;
    }
    function compute() {
      const w = panelEl!.offsetWidth;
      const h = panelEl!.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let left = scrimInsetLeft + EDGE_GAP;
      left = Math.min(left, vw - w - EDGE_GAP);
      left = Math.max(EDGE_GAP, left);

      const top = Math.max(EDGE_GAP, vh - h - EDGE_GAP);

      setPos({ top, left });
    }
    compute();
    window.addEventListener("resize", compute);
    const ro = new ResizeObserver(compute);
    ro.observe(panelEl);
    return () => {
      window.removeEventListener("resize", compute);
      ro.disconnect();
    };
  }, [isMobile, open, panelEl, scrimInsetLeft]);

  function handleAnimationEnd(e: AnimationEvent<HTMLElement>) {
    if (e.target !== e.currentTarget) return;
    if (closing) {
      setMounted(false);
      setClosing(false);
    }
  }

  function handleMarkRead(n: AppNotification) {
    if (n.read) return;
    setItems((prev) =>
      prev ? prev.map((item) => (item.id === n.id ? { ...item, read: true } : item)) : prev,
    );
    onUnreadCount?.(items ? items.filter((item) => item.id !== n.id && !item.read).length : 0);
    void markNotificationRead(n.id);
  }

  function handleDownload(n: AppNotification) {
    void downloadNotificationFile(n);
    handleMarkRead(n);
  }

  if (!mounted || typeof document === "undefined") return null;

  const state = closing ? "closed" : "open";

  const body = (
    <>
      <header className="flex shrink-0 items-center gap-3">
        <h2
          id="notifications-popover-title"
          className="min-w-0 flex-1 text-body font-semibold text-black"
        >
          Notifications
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close notifications"
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-[4px] text-grey-900 transition-colors hover:text-black"
        >
          <Icon name="close_small" size={24} />
        </button>
      </header>

      <div aria-hidden className="h-px w-full shrink-0 bg-grey-200" />

      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
        {status === "loading" ? (
          <NotificationsSkeleton />
        ) : status === "error" ? (
          <ErrorState onRetry={() => void load()} />
        ) : items && items.length > 0 ? (
          <ul className="flex w-full flex-col gap-1">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onMarkRead={() => handleMarkRead(n)}
                onDownload={() => handleDownload(n)}
              />
            ))}
          </ul>
        ) : (
          <EmptyState />
        )}
      </div>
    </>
  );

  const scrim = (
    <div
      className="notif-scrim fixed inset-y-0 right-0 z-40 bg-grey-900-24"
      data-state={state}
      aria-hidden
      onClick={onClose}
      style={{ left: isMobile ? 0 : scrimInsetLeft }}
    />
  );

  if (isMobile) {
    return createPortal(
      <>
        {scrim}
        <aside
          ref={setPanelEl}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notifications-popover-title"
          data-state={state}
          onAnimationEnd={handleAnimationEnd}
          style={{ maxHeight: "85vh" }}
          className="notif-sheet fixed inset-x-0 bottom-0 z-50 flex flex-col gap-4 rounded-t-[12px] bg-white p-4 shadow-soft outline-none"
        >
          {body}
        </aside>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      {scrim}
      <aside
        ref={setPanelEl}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notifications-popover-title"
        data-state={state}
        onAnimationEnd={handleAnimationEnd}
        style={{
          top: pos?.top ?? 0,
          left: pos?.left ?? 0,
          width: FLYOUT_WIDTH,
          maxWidth: "calc(100vw - 16px)",
          minHeight: FLYOUT_MIN_HEIGHT,
          maxHeight: FLYOUT_MAX_HEIGHT,
          visibility: pos ? "visible" : "hidden",
        }}
        className="notif-flyout fixed z-50 flex flex-col gap-4 rounded-[4px] bg-white p-4 shadow-soft outline-none"
      >
        {body}
      </aside>
    </>,
    document.body,
  );
}

function NotificationRow({
  notification,
  onMarkRead,
  onDownload,
}: {
  notification: AppNotification;
  onMarkRead: () => void;
  onDownload: () => void;
}) {
  const { heading, subheading, date, action, read } = notification;
  const isDownload = action === "download";

  return (
    <li
      className={[
        "flex items-start gap-1.5 rounded-[4px] border border-grey-200 p-2 shadow-flat",
        read ? "bg-grey-100" : "bg-white",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={["mt-2 size-2 shrink-0 rounded-full", read ? "" : "bg-green-400"].join(" ")}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1 py-1.5 pr-1.5">
        <div className="flex items-start gap-2">
          <h3
            className={[
              "min-w-0 flex-1 text-[15px] font-semibold [word-break:break-word]",
              read ? "text-grey-600" : "text-black",
            ].join(" ")}
          >
            {heading}
          </h3>
          {isDownload ? (
            <button
              type="button"
              onClick={onDownload}
              aria-label={`Download ${heading}`}
              className={[
                "-mr-1 -mt-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors hover:bg-grey-100 hover:text-black hover:shadow-flat",
                read ? "border-transparent text-grey-300" : "border-grey-200 text-grey-900",
              ].join(" ")}
            >
              <Icon name="download" size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onMarkRead}
              disabled={read}
              aria-label={read ? `${heading} already read` : `Mark ${heading} as read`}
              className="-mr-1 -mt-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-grey-200 transition-colors disabled:cursor-default disabled:border-transparent disabled:text-grey-300 text-grey-900 hover:bg-grey-100 hover:text-black hover:shadow-flat disabled:hover:bg-transparent disabled:hover:shadow-none"
            >
              <Icon name="done" size={20} />
            </button>
          )}
        </div>
        <div className="flex items-end gap-3">
          <Tooltip description={subheading} wrapperClassName="min-w-0 flex-1">
            <p className="truncate text-small text-grey-600">{subheading}</p>
          </Tooltip>
          <p className="shrink-0 whitespace-nowrap text-tiny font-medium text-grey-900">{date}</p>
        </div>
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-2 px-4 py-8 text-center">
      <Icon name="notifications" size={24} className="text-grey-300" />
      <p className="text-small font-medium text-grey-900">You&rsquo;re all caught up</p>
      <p className="text-small text-grey-500">New reports and alerts will show up here.</p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-2 px-4 py-8 text-center">
      <Icon name="cloud_off" size={24} className="text-grey-500" />
      <p className="text-small font-medium text-grey-900">Couldn&rsquo;t load notifications</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-small font-semibold text-green-700 underline-offset-2 transition-colors hover:underline"
      >
        Try again
      </button>
    </div>
  );
}

function NotificationsSkeleton() {
  return (
    <ul aria-hidden className="flex w-full flex-col gap-1">
      {[0, 1].map((i) => (
        <li
          key={i}
          className="flex animate-pulse items-start gap-1.5 rounded-[4px] border border-grey-200 bg-white p-2"
        >
          <span className="mt-2 size-1.5 shrink-0 rounded-full bg-grey-200" />
          <div className="flex min-w-0 flex-1 flex-col gap-2 py-1.5 pr-1.5">
            <div className="h-3.5 w-2/5 rounded-[4px] bg-grey-200" />
            <div className="h-3 w-4/5 rounded-[4px] bg-grey-100" />
          </div>
        </li>
      ))}
    </ul>
  );
}
